import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import FAQ, { getFAQSchema } from '@/pages/home/components/FAQ';
import { useEffect } from 'react';

const siteUrl = (typeof import.meta !== 'undefined' && import.meta.env?.VITE_SITE_URL) || 'https://biffdigital.com';
const pagePath = '/sektorler/siyasi-kampanya';
const pageUrl = `${siteUrl}${pagePath}`;

const siyasiFAQItems = [
  {
    question: 'Siyasi kampanyada dijital pazarlama ne işe yarar?',
    answer:
      'Seçmenin büyük bir bölümü artık sosyal medyada ve arama motorlarında kararını şekillendiriyor. Dijital pazarlama; doğru seçmene doğru mesajı ulaştırır, kampanyanın anlatısını disiplinli biçimde kurar ve saha çalışmasına veri sağlar. Ancak dijital, tek başına bir strateji değil, bütüncül kampanyanın bir parçasıdır.',
  },
  {
    question: 'Seçmen analizi nasıl yapılır?',
    answer:
      'Seçmeni demografiden ibaret görmeyiz. Değerler, endişeler, karar nedenleri ve mesaj duyarlılıkları üzerinden segmentlere ayırırız. Hangi seçmen grubu hangi mesaja tepki veriyor, hangi kanaldan ulaşıyor — veriyle haritalarız. Kampanya buna göre kişiselleştirilir.',
  },
  {
    question: 'Kampanya mesajı nasıl kurgulanır?',
    answer:
      'Mesaj önce stratejide kurulur. Adayın veya partinin söylediğiyle seçmenin anladığı arasında fark olmamalı. Tek bir ana anlatı belirler, tüm kanallarda bu anlatıyı disiplinle tekrarlarız. Karmaşık vaatleri değil, seçmenin hatırlayacağı net ve güçlü bir mesajı öne çıkarırız.',
  },
  {
    question: 'Kriz anında iletişim nasıl yönetilir?',
    answer:
      'Kriz, siyasi kampanyalarda kaçınılmazdır. Önemli olan krize hazırlıklı olmaktır. Olası senaryoları önceden çalışır, hızlı yanıt mekanizması kurarız. Kriz anında sessizlik en kötü stratejidir; doğru mesajı doğru zamanda vermek anlatının kontrolünü elinde tutmanızı sağlar.',
  },
  {
    question: 'Siyasi kampanya bütçesi nasıl planlanır?',
    answer:
      'Bütçeyi kampanyanın aşamalarına ve hedef seçmen gruplarına göre dağıtırız. Tek bir kanala yüklenmek yerine; farkındalık, ikna ve harekete geçirme aşamalarında farklı kanalları dengeli kullanırız. Ölçülebilir hedeflerle harcamanın karşılığını sürekli izleriz.',
  },
  {
    question: 'Dijital kampanya ile saha çalışması nasıl entegre edilir?',
    answer:
      'Dijital, sahayı güçlendirir. Dijital kampanyadan gelen veri, saha ekibine hangi bölgede hangi mesajın çalıştığını gösterir. Saha ekibinin topladığı geri bildirim de dijital kampanyayı besler. Bu iki cephe aynı masada yönetilmezse kampanya yarım kalır.',
  },
];

const faqSchema = getFAQSchema(siyasiFAQItems);

const pageSchema = {
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'Service',
      name: 'Siyasi Kampanya Yönetimi Hizmeti',
      provider: {
        '@type': 'Organization',
        name: 'BIFF Digital',
        url: siteUrl,
        telephone: '+905557101968',
        address: {
          '@type': 'PostalAddress',
          streetAddress: 'Söğütözü Cad. No: 2 Koç Kuleleri C Blok 17',
          addressLocality: 'Çankaya',
          addressRegion: 'Ankara',
          addressCountry: 'TR',
          postalCode: '06530',
        },
      },
      description:
        'Siyasi kampanyalar için seçmen analizi, mesaj disiplini, dijital kampanya ve kriz iletişimiyle bütüncül kampanya yönetimi. BIFF Digital siyasi iletişimi strateji ve veriyle yürütür.',
      url: pageUrl,
      areaServed: { '@type': 'Country', name: 'Turkey' },
      serviceType: 'Siyasi Kampanya Yönetimi',
      hasOfferCatalog: {
        '@type': 'OfferCatalog',
        name: 'Siyasi Kampanya Hizmet Detayları',
        itemListElement: [
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Kampanya Stratejisi', description: 'Seçim hedefine uygun bütüncül kampanya planı' } },
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Seçmen Analizi', description: 'Değer ve davranış temelli seçmen segmentasyonu' } },
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Mesaj Disiplini', description: 'Tek anlatı üzerinden tutarlı iletişim kurulumu' } },
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Kriz İletişimi', description: 'Hızlı yanıt ve anlatı kontrolü mekanizması' } },
        ],
      },
    },
    {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Ana Sayfa', item: siteUrl },
        { '@type': 'ListItem', position: 2, name: 'Sektörler', item: `${siteUrl}/sektorler/siyasi-kampanya` },
        { '@type': 'ListItem', position: 3, name: 'Siyasi Kampanya', item: pageUrl },
      ],
    },
    {
      '@type': 'SpeakableSpecification',
      cssSelector: ['.speakable-summary'],
    },
    faqSchema,
  ],
};

export default function SiyasiKampanya() {
  useEffect(() => {
    document.title = 'Siyasi Kampanya Yönetimi | Seçmen Analizi & Dijital İletişim | BIFF Digital';

    let link = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!link) {
      link = document.createElement('link');
      link.setAttribute('rel', 'canonical');
      document.head.appendChild(link);
    }
    link.setAttribute('href', pageUrl);

    return () => {
      link?.setAttribute('href', siteUrl);
    };
  }, []);

  return (
    <div className="min-h-screen bg-background-50">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(pageSchema) }}
      />
      <Navbar />
      <main className="pt-16 md:pt-20">

        {/* HERO */}
        <section className="relative w-full h-[420px] md:h-[560px] overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="https://readdy.ai/api/search-image?query=Abstract%20cinematic%20concept%20of%20a%20political%20campaign%20with%20dramatic%20crowd%20silhouettes%20and%20stage%20spotlights%20in%20deep%20red%20and%20dark%20charcoal%20tones%2C%20large%20audience%20gathered%20at%20a%20rally%2C%20powerful%20atmosphere%2C%20premium%20editorial%20style%2C%20dramatic%20backlighting%2C%20no%20identifiable%20faces%2C%20dark%20moody%20high%20contrast&width=1440&height=800&seq=siyasi-hero-01&orientation=landscape"
              alt="Siyasi kampanya yönetimi - seçmen analizi ve dijital iletişim stratejisi"
              title="Siyasi Kampanya Yönetimi - BIFF Digital"
              width="1440"
              height="800"
              className="w-full h-full object-cover object-center"
              fetchPriority="high"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black/70" />
          </div>

          <div className="relative z-10 h-full flex flex-col items-center justify-center text-center px-4 sm:px-6 lg:px-8">
            <p className="text-primary-500 font-bold text-sm tracking-[0.2em] uppercase mb-4">SEKTÖRLER</p>
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl text-foreground-50 tracking-tighter leading-none mb-6">
              Siyasi Kampanya
            </h1>
            <p className="text-xl md:text-2xl text-foreground-200 font-medium leading-relaxed mb-4">
              Seçim Bir Reklam Değil.<br />
              <span className="text-primary-500">Bir Anlatıdır.</span>
            </p>
            <p className="text-sm md:text-base text-foreground-300 leading-relaxed max-w-2xl speakable-summary">
              Siyasi kampanya; seçmeni anlamak, tek bir anlatıyı disiplinle kurmak ve o anlatıyı her kanalda tutarlı biçimde taşımaktır. Biz kampanyayı masada kazanılan bir strateji olarak yönetiyoruz.
            </p>
          </div>
        </section>

        {/* Kampanya Masada Kazanılır */}
        <section className="section-padding">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="font-heading text-3xl md:text-4xl text-foreground-50 tracking-tighter mb-8">
              Kampanya Önce Medyada Değil,<br />Masada Kazanılır.
            </h2>
            <p className="text-sm md:text-base text-foreground-400 leading-relaxed mb-6">
              Adayın söylediği ile seçmenin duyduğu arasındaki fark, kampanyanın kaderini belirler. Bu yüzden önce seçmeni anlar, sonra mesajı kurar, en sonda medyaya çıkarız. Reklam bütçesi bir anlatıyı büyütebilir; ama yanlış kurulmuş bir anlatıyı düzeltemez.
            </p>
            <div className="glass-card rounded-2xl p-6 md:p-8 border-l-4 border-primary-500 mb-8">
              <p className="text-base md:text-lg text-foreground-50 font-bold leading-relaxed">
                Anlatıyı kontrol eden, seçimi kontrol eder.
              </p>
              <p className="text-sm text-foreground-300 mt-3 leading-relaxed">
                Rakibin gündeminde değil, kendi anlatınızın gündeminde kalmak kazandırır.
              </p>
            </div>
          </div>
        </section>

        {/* Ne Yapıyoruz */}
        <section className="section-padding bg-background-100">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="mb-10 md:mb-14">
              <p className="text-primary-500 font-bold text-sm tracking-widest uppercase mb-2">NE YAPIYORUZ?</p>
              <h2 className="font-heading text-3xl md:text-4xl text-foreground-50 tracking-tighter">
                Seçmenden Sandığa<br />Bütüncül Kampanya Yönetimi
              </h2>
            </div>
            <div className="grid md:grid-cols-2 gap-5 md:gap-6">
              {[
                { icon: 'ri-flag-2-line', title: 'Kampanya Stratejisi', desc: 'Seçim hedefini, seçmen haritasını ve rakip konumlanmasını analiz ederek bütüncül kampanya planını kuruyoruz. Her aşama için hedef, mesaj ve kanal netleşiyor.' },
                { icon: 'ri-pie-chart-line', title: 'Seçmen Analizi & Segmentasyon', desc: 'Seçmeni demografiden öte, değerleri ve endişeleriyle anlıyoruz. Hangi grup hangi mesaja duyarlı, hangi kanaldan ulaşıyor — veriyle haritalıyoruz.' },
                { icon: 'ri-chat-quote-line', title: 'Mesaj Disiplini', desc: 'Tek bir ana anlatı belirliyor, tüm kanallarda tutarlı biçimde tekrarlıyoruz. Karmaşık vaatler yerine seçmenin hatırlayacağı net ve güçlü bir mesaj kuruyoruz.' },
                { icon: 'ri-smartphone-line', title: 'Dijital Kampanya', desc: 'Sosyal medya, arama motorları ve dijital reklam kanallarını hedef seçmen gruplarına göre yönetiyoruz. Doğru seçmene, doğru zamanda, doğru mesajı ulaştırıyoruz.' },
                { icon: 'ri-shield-line', title: 'Kriz İletişimi', desc: 'Olası kriz senaryolarını önceden çalışıyor, hızlı yanıt mekanizması kuruyoruz. Kriz anında anlatının kontrolünü elinizde tutmanızı sağlıyoruz.' },
                { icon: 'ri-database-2-line', title: 'Veri & Saha Entegrasyonu', desc: 'Dijitalden gelen veri sahaya, sahadan gelen geri bildirim dijitale akar. İki cepheyi aynı masada yöneterek kampanyanın tamamını ölçüyoruz.' },
              ].map((item, i) => (
                <div key={i} className="glass-card-hover rounded-2xl p-6 md:p-8 group">
                  <div className="w-12 h-12 bg-background-200 rounded-xl flex items-center justify-center mb-5 group-hover:bg-primary-500/10 transition-colors duration-300">
                    <i className={`${item.icon} text-xl text-primary-500`} />
                  </div>
                  <h3 className="text-lg font-bold text-foreground-50 mb-3">{item.title}</h3>
                  <p className="text-sm text-foreground-400 leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Süreç */}
        <section className="section-padding bg-background-50">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="font-heading text-3xl md:text-4xl text-foreground-50 tracking-tighter mb-10">
              Kampanya Sürecimiz
            </h2>
            <div className="space-y-6">
              {[
                { step: '01', title: 'Seçmen & Rakip Analizi', desc: 'Hedef seçmen gruplarını ve rakip konumlanmasını analiz ediyoruz. Kimin, neden, hangi mesajla ikna olacağını veriyle görüyoruz.' },
                { step: '02', title: 'Anlatı & Mesaj Kurulumu', desc: 'Tek bir ana anlatı ve onu destekleyen mesaj setini kuruyoruz. Söylenen ile anlaşılan arasındaki farkı kapatıyoruz.' },
                { step: '03', title: 'Çok Kanallı Yürütme', desc: 'Dijital reklam, sosyal medya, içerik ve saha çalışmasını aynı anlatı etrafında yürütüyoruz. Her kanal aynı mesajı disiplinle taşıyor.' },
                { step: '04', title: 'Ölçüm & Optimizasyon', desc: 'Kampanya boyunca veriyi canlı izliyor, çalışan mesaj ve kanallara ağırlık veriyoruz. Sandığa giden yolda her gün optimize ediyoruz.' },
              ].map((item, i) => (
                <div key={i} className="glass-card rounded-2xl p-5 md:p-6 flex items-start gap-5">
                  <span className="text-2xl font-black text-primary-500 font-heading shrink-0">{item.step}</span>
                  <div>
                    <h3 className="text-base font-bold text-foreground-100 mb-1">{item.title}</h3>
                    <p className="text-sm text-foreground-400 leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <FAQ
          items={siyasiFAQItems}
          sectionTitle="SIK SORULANLAR"
          sectionSubtitle="SİYASİ KAMPANYA"
          sectionSummary="Siyasi kampanya yönetimi hakkında en sık sorulan soruları, doğrudan ve net cevaplarla burada topladık."
          speakableClass={false}
        />

        {/* CTA */}
        <section className="section-padding bg-background-50">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="glass-card rounded-2xl p-8 md:p-12 text-center relative overflow-hidden">
              <div className="absolute top-0 right-0 w-64 h-64 bg-primary-500/5 rounded-bl-full -mr-16 -mt-16" />
              <div className="relative z-10">
                <h2 className="font-heading text-2xl md:text-3xl lg:text-4xl text-foreground-50 tracking-tighter mb-4">
                  Büyük bir fikir varsa, onu büyük bir<br />
                  <span className="text-primary-500">kampanyaya dönüştürelim.</span>
                </h2>
                <p className="text-sm md:text-base text-foreground-400 mb-8 max-w-xl mx-auto leading-relaxed">
                  Seçmeninizi, anlatınızı ve kazanma yolunuzu birlikte konuşalım. Önce strateji, sonra kampanya.
                </p>
                <a href="/iletisim" className="btn-primary text-base px-8 py-4">
                  Strateji Görüşmesi Planla
                  <i className="ri-arrow-right-line" />
                </a>
              </div>
            </div>
          </div>
        </section>

      </main>
      <Footer />
    </div>
  );
}